<template>
  <v-container fluid class="red-darken-3 ma-0">
    <!---------------------------------------REGULAR SEARCH---------------------------------------->
    <v-row class="justify-center">
      <v-col
        xs="12"
        sm="6"
        md="4"
        :class="{ 'pr-1': $vuetify.display.smAndUp }"
      >
        <!--SEARCH BY TITLE-->
        <v-autocomplete
          v-if="searchType === 'title'"
          v-model="titleCode"
          v-model:search-input="searchTitle"
          :loading="loadingTitle"
          :items="titles"
          :label="$t('reservations.parts.search')"
          :item-title="`title.${$store.getters.language}`"
          item-value="partCode"
          flat
          variant="solo"
          color="grey-darken-4"
          append-inner-icon="mdi-magnify"
          single-line
          hide-details
          hide-no-data
          class="ml-2 mr-2"
          @keyup.enter="search()"
          @click:append="search()"
        />
        <!--SEARCH FIELD-->
        <v-text-field
          v-else
          v-model="searchField"
          :label="$t('reservations.parts.search')"
          flat
          variant="solo"
          color="grey-darken-4"
          append-inner-icon="mdi-magnify"
          single-line
          hide-details
          class="ml-2 mr-2"
          @keyup.enter="search()"
          @click:append="search()"
        />
      </v-col>

      <v-col
        xs="12"
        sm="3"
        md="2"
        xl="1"
        :class="{ 'pl-1': $vuetify.display.smAndUp }"
      >
        <v-select
          v-model="searchType"
          :items="searchTypes"
          item-title="label"
          item-value="value"
          color="grey-darken-4"
          variant="solo"
          flat
          hide-details
          class="ml-2 mr-2"
          :class="{ 'mt-2': $vuetify.display.xs }"
        />
      </v-col>
    </v-row>
    <!---------------------------------------ADVANCED SEARCH--------------------------------------->
    <v-row row color="red-darken-3" class="pb-2" wrap>
      <!-------------------------------------------BRAND------------------------------------------->
      <v-col xs="12" sm="6" xl="3">
        <v-autocomplete
          v-model="brand"
          :items="brands"
          :label="$t('parts.advanced_search.brand')"
          item-title="title"
          flat
          variant="solo"
          color="red-darken-1"
          class="ml-2 mr-2 mt-2"
          hide-details
          return-object
        >
          <template #item="{ item }">
            <v-row row wrap align-center justify-center>
              <v-col xs="3" sm="2" lg="1">
                <v-img :src="item.logo" max-height="35" max-width="35" />
              </v-col>
              <v-col xs="9" sm="10" lg="11">
                <span class="mt-2"> {{ item.title }} </span>
              </v-col>
            </v-row>
          </template>
          <template #selection="{ item }">
            <v-row class="justify-center">
              <v-col xs="3" lg="2">
                <v-img :src="item.logo" max-height="35" max-width="35" />
              </v-col>
              <v-col xs="9" lg="10">
                <span> {{ item.title }} </span>
              </v-col>
            </v-row>
          </template>
        </v-autocomplete>
      </v-col>
      <!-------------------------------------------MODEL------------------------------------------->
      <v-col xs="12" sm="6" xl="3">
        <v-combobox
          v-model="model"
          :items="models"
          :label="!model ? $t('parts.advanced_search.model') : ''"
          item-title="description"
          item-value="modelCode"
          variant="solo"
          color="red-darken-1"
          hide-details
          return-object
          clearable
          @click:append-inner="model = null"
        >
          <!-- Dropdown items -->
          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <v-list-item-content>
                <v-row>
                  <!-- Description -->
                  <v-col cols="12" md="4" class="bg-grey-lighten-3 mt-2">
                    <div class="d-flex flex-column">
                      <span class="caption">{{
                        $t("parts.advanced_search.model")
                      }}</span>
                      <span class="font-weight-bold">{{
                        item.raw.description
                      }}</span>
                    </div>
                  </v-col>

                  <!-- Year range -->
                  <v-col
                    cols="12"
                    md="8"
                    class="d-none d-md-flex bg-grey-lighten-3 mt-2"
                  >
                    <div class="d-flex flex-column">
                      <span class="caption">{{
                        $t("parts.advanced_search.year")
                      }}</span>
                      <span class="font-weight-bold">
                        {{ formatMonthYear(item.raw.startingDate) }} -
                        {{ formatMonthYear(item.raw.endingDate) }}
                      </span>
                    </div>
                  </v-col>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </template>

          <!-- Selected value -->
          <template #selection="{ item }">
            <div class="d-flex justify-between align-center pa-2">
              <div class="d-flex flex-column pr-2">
                <span class="caption">{{
                  $t("parts.advanced_search.model")
                }}</span>
                <span class="font-weight-bold">{{ item.raw.description }}</span>
              </div>
              <div class="d-none d-md-flex flex-column">
                <span class="caption">{{
                  $t("parts.advanced_search.year")
                }}</span>
                <span class="font-weight-bold">
                  {{ formatMonthYear(item.raw.startingDate) }} -
                  {{ formatMonthYear(item.raw.endingDate) }}
                </span>
              </div>
            </div>
          </template>
        </v-combobox>
      </v-col>
      <!------------------------------------------VERSION------------------------------------------>
      <v-col xs="12" xl="6">
        <v-autocomplete
          v-model="engine"
          :items="versions"
          :label="$t('parts.advanced_search.engine')"
          item-title="engineCode"
          flat
          variant="solo"
          color="red-darken-1"
          hide-details
          clearable
          @click:append-inner="
            engine = null;
            version = null;
            if (model) getVersions(model.brandCode, model.modelCode);
          "
        />
      </v-col>
      <!---------------------------------------INSTITUTIONS---------------------------------------->
      <v-col
        v-if="
          $store.getters.role === 'LekoAdmin' ||
          $store.getters.role === 'LekoEmployee' ||
          $store.getters.role === 'SuperAdmin'
        "
      >
        <v-autocomplete
          v-model="advancedSearchItems.institution"
          :items="institutions"
          :loading="loadingInstitution"
          :label="$t('parts.advanced_search.institution')"
          item-title="title"
          item-value="_id"
          flat
          variant="solo"
          color="red-darken-1"
          class="ml-2 mr-2 mt-2"
          hide-details
        />
      </v-col>
      <!-----------------------------------------SHIPMENTS----------------------------------------->
      <v-col>
        <v-autocomplete
          v-model="advancedSearchItems.shipment"
          :items="shipments"
          :loading="loadingShipment"
          :label="$t('parts.advanced_search.shipment')"
          item-title="name"
          item-value="_id"
          flat
          variant="solo"
          color="red-darken-1"
          class="ml-2 mr-2 mt-2"
          hide-details
        />
      </v-col>
      <!------------------------------------------STATUS------------------------------------------->
      <v-col xs="12" md="12" lg="12">
        <v-select
          v-model="advancedSearchItems.status"
          :items="statusItems"
          :label="$t('parts.advanced_search.status')"
          :hint="$t('parts.advanced_search.select_status')"
          item-title="title"
          item-value="value"
          variant="solo"
          flat
          chips
          closable-chips
          multiple
          color="red-darken-2"
          class="ml-2 mr-2 mt-2"
          persistent-hint
        />
      </v-col>
      <!------------------------------------------QUALITY------------------------------------------>
      <v-col xs="12" md="12" lg="12">
        <v-select
          v-model="advancedSearchItems.quality"
          :items="qualityItems"
          :loading="loadingQuality"
          :label="$t('parts.advanced_search.quality')"
          :hint="$t('parts.advanced_search.select_quality')"
          item-value="_id"
          item-title="title"
          variant="solo"
          flat
          chips
          multiple
          color="red-darken-2"
          class="ml-2 mr-2"
          persistent-hint
        >
          <template #item="{ item }">
            <v-chip small>
              <v-rating
                v-model="item.rating"
                :color="
                  advancedSearchItems.quality.includes(item._id)
                    ? 'red'
                    : 'grey'
                "
                small
                :background-color="
                  advancedSearchItems.quality.includes(item._id)
                    ? 'red'
                    : 'grey'
                "
                readonly
              />
            </v-chip>
          </template>

          <template #selection="{ item }">
            <v-chip small>
              <v-rating
                v-model="item.rating"
                color="red"
                small
                background-color="red"
                readonly
              />
            </v-chip>
          </template>
        </v-select>
      </v-col>
      <!-------------------------------------------PRICE------------------------------------------->
      <v-col xs="12" md="6">
        <v-row class="justify-center">
          <v-col xs="8" sm="9" xl="10">
            <v-range-slider
              v-model="advancedSearchItems.price"
              :label="$t('parts.advanced_search.price_range')"
              :max="50000"
              :min="0"
              :step="1000"
              color="grey-darken-4"
              class="pl-2 pr-3"
            />
          </v-col>
          <v-col xs="4" sm="3" xl="2">
            <span class="pl-3 pr-2">
              {{ advancedSearchItems.price[0] }} -
              {{ advancedSearchItems.price[1] }}
            </span>
          </v-col>
        </v-row>
      </v-col>
      <!-------------------------------------------DATE-------------------------------------------->
      <!--STARTING DATE-->
      <v-col xs="12" sm="6" md="3">
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template #activator="{ on }">
            <v-text-field
              v-model="advancedSearchItems.startDate"
              :label="$t('parts.advanced_search.starting_date')"
              :hint="$t('parts.advanced_search.select_starting_date')"
              variant="solo"
              flat
              color="black"
              persistent-hint
              prepend-icon="event"
              readonly
              class="ml-2 mr-2"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="advancedSearchItems.startDate"
            color="red-darken-3"
            @input="startDateMenu = false"
          />
        </v-menu>
      </v-col>
      <!--ENDING DATE-->
      <v-col xs="12" sm="6" md="3">
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template #activator="{ on }">
            <v-text-field
              v-model="advancedSearchItems.endDate"
              :label="$t('parts.advanced_search.ending_date')"
              :hint="$t('parts.advanced_search.select_ending_date')"
              variant="solo"
              flat
              color="black"
              persistent-hint
              prepend-icon="event"
              readonly
              class="ml-2 mr-2"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="advancedSearchItems.endDate"
            color="red-darken-3"
            @input="endDateMenu = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <!--------------------------------------------------------------------------------------------->
    <v-row class="justify-center pb-4">
      <v-btn dark color="grey-darken-4" class="mx-1" @click="clear">
        {{ $t("parts.advanced_search.clear") }}
        <v-icon right> clear_all </v-icon>
      </v-btn>
      <v-btn dark color="grey-darken-4" class="mx-1" @click="search">
        {{ $t("parts.advanced_search.search") }}
        <v-icon right> search </v-icon>
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import moment from "moment";
import getRequest from "../../../api/getRequest";
import { useDisplay } from "vuetify";

export default {
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },
  data: () => ({
    searchTypes: [
      { label: "Title", value: "title" },
      { label: "ID", value: "id" },
      { label: "Other", value: "other" },
    ],
    searchType: "title",
    searchField: "",
    searchTitle: null,
    titleCode: null,
    titles: [],
    brands: [],
    models: [],
    versions: [],
    brand: null,
    model: null,
    version: null,
    advancedSearchItems: {
      brand: null,
      model: null,
      version: null,
      institution: null,
      shipment: null,
      status: [1, 2, 2.1, 3, 4],
      quality: [],
      price: [0, 50000],
      startDate: "",
      endDate: "",
    },
    statusItems: [
      { title: "Entered", value: 1 },
      { title: "Returned", value: 2 },
      { title: "Returned-Shop", value: 2.1 },
      { title: "Re-Entered", value: 3 },
      { title: "Found", value: 4 },
    ],
    qualityItems: [],
    institutions: [],
    shipments: [],
    loadingTitle: false,
    loadingBrand: false,
    loadingModel: false,
    loadingVersion: false,
    loadingQuality: false,
    loadingInstitution: false,
    loadingShipment: false,
    startDateMenu: false,
    endDateMenu: false,
  }),
  computed: {
    display() {
      return useDisplay();
    },
  },
  watch: {
    searchTitle(value) {
      if (value) {
        this.loadingTitle = true;
        getRequest(
          "/info-car/parts",
          `title=${value}&language=${this.$store.getters.language}`,
        )
          .then((response) => {
            this.titles = response.parts;
          })
          .catch(() => {})
          .finally(() => {
            this.loadingTitle = false;
          });
      } else {
        this.titleCode = null;
      }
    },
    "advancedSearchItems.institution": {
      handler(value) {
        if (value) this.getShipments(value);
      },
    },
    brand(value) {
      if (value) {
        this.models = [];
        this.model = null;
        this.versions = [];
        this.version = null;
        this.getModels(value.brandCode);
        this.advancedSearchItems.brand = value.brandCode;
      }
    },
    model(value) {
      if (value) {
        this.versions = [];
        this.version = null;
        this.getVersions(value.brandCode, value.modelCode);
        this.advancedSearchItems.model = value.modelCode;
      }
    },
    version(value) {
      if (value) {
        this.advancedSearchItems.version = value.versionCode;
      }
    },
  },
  mounted() {
    this.getLocale();
    this.getQualities();
    this.getBrands();
    if (
      this.$store.getters.role === "LekoAdmin" ||
      this.$store.getters.role === "LekoEmployee" ||
      this.$store.getters.role === "SuperAdmin"
    ) {
      this.getInstitutions();
    } else {
      this.getShipments(this.$store.getters.institutionId);
    }
  },
  methods: {
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
    search() {
      this.$emit("search", {
        type: this.searchType,
        searchField: this.searchField,
        titleCode: this.titleCode,
        advancedSearchFields: this.advancedSearchItems,
      });
    },
    clear() {
      this.searchField = "";
      this.advancedSearchItems = {
        brand: null,
        model: null,
        version: null,
        institution: null,
        shipment: null,
        status: [1, 2, 2.1, 3, 4],
        quality: [],
        price: [0, 50000],
        startDate: "",
        endDate: "",
      };
      this.brand = null;
      this.model = null;
      this.version = null;
      this.qualityItems.forEach((quality) =>
        this.advancedSearchItems.quality.push(quality._id),
      );
      this.$emit("clear");
    },
    getLocale() {
      this.searchTypes[0].label = this.$t("reservations.parts.title");
      this.searchTypes[1].label = this.$t("reservations.parts.id");
      this.searchTypes[2].label = this.$t("reservations.parts.other");
      this.statusItems[0].title = this.$t("parts.status.entered");
      this.statusItems[1].title = this.$t("parts.status.returned");
      this.statusItems[2].title = this.$t("parts.status.returned_shop");
      this.statusItems[3].title = this.$t("parts.status.re_entered");
      this.statusItems[4].title = this.$t("parts.status.found");
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },

    getBrands() {
      this.loadingBrand = true;
      getRequest("/info-car/brands")
        .then((response) => {
          this.brands = response.brands;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingBrand = false;
        });
    },
    getModels(brandCode) {
      this.loadingModel = true;
      getRequest("/info-car/models", `brand_code=${brandCode}`)
        .then((response) => {
          this.models = response.models;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingModel = false;
        });
    },
    getVersions(brandCode, modelCode) {
      this.loadingVersion = true;
      getRequest(
        "/info-car/versions",
        `brand_code=${brandCode}&model_code=${modelCode}`,
      )
        .then((response) => {
          this.versions = response.versions;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingVersion = false;
        });
    },

    getQualities() {
      this.loadingQuality = true;
      getRequest("/quality/")
        .then((response) => {
          this.qualityItems = response.qualities;
          this.advancedSearchItems.quality = [];
          this.qualityItems.forEach((quality) =>
            this.advancedSearchItems.quality.push(quality._id),
          );
          this.loadingQuality = false;
        })
        .catch(() => {
          this.loadingQuality = false;
        });
    },
    getInstitutions() {
      this.loadingInstitution = true;
      getRequest("/institution/")
        .then((response) => {
          this.institutions = response.institutions;
          this.loadingInstitution = false;
        })
        .catch(() => {
          this.loadingInstitution = false;
        });
    },
    getShipments(institutionId) {
      this.loadingShipment = true;
      getRequest("/shipment/institution", `id=${institutionId}`)
        .then((response) => {
          this.shipments = response.shipments;
          this.loadingShipment = false;
        })
        .catch(() => {
          this.loadingShipment = false;
        });
    },
  },
};
</script>
